package com.dotdrives.web.pages.donor

import com.dotdrives.controller.*
import com.dotdrives.model.*
import com.dotdrives.web.ui.DotButton
import react.*
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.h3
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.label
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.section
import react.dom.html.ReactHTML.textarea
import web.cssom.ClassName
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.tel
import web.html.text

external interface DonorContactProps : Props {
    var profile: DonorProfile
    var busy: Boolean
    var onSave: (String, String) -> Unit
    var onClose: () -> Unit
}

val DonorContact =
    FC<DonorContactProps> { props ->
        val (phone, setPhone) = useState(props.profile.phone.orEmpty())
        val (address, setAddress) = useState(props.profile.address.orEmpty())
        section {
            className = ClassName("donor-panel donor-form")
            h2 { +"Your contact details" }
            p { +"${props.profile.displayName} · ${props.profile.email}" }
            p {
                +"Contact the organization to correct your name or giving email. This form does not change your verified sign-in identity."
            }
            label {
                +"Phone"
                input {
                    value = phone
                    type = InputType.tel
                    maxLength = 80
                    onChange = { setPhone(it.currentTarget.value) }
                }
            }
            label {
                +"Mailing address"
                textarea {
                    value = address
                    maxLength = 1000
                    onChange = { setAddress(it.currentTarget.value) }
                }
            }
            div {
                className = ClassName("donor-actions")
                DotButton {
                    text = "Save contact details"
                    enabled = !(props.busy)
                    onClick = { props.onSave(phone, address) }
                }
                button {
                    type = ButtonType.buttonType
                    onClick = { props.onClose() }
                    +"Close"
                }
            }
        }
    }

external interface DonorRecurringControlsProps : Props {
    var plan: DonorSubscription
    var state: DonorUiState
    var controller: DonorController
}

val DonorRecurringControls =
    FC<DonorRecurringControlsProps> { props ->
        val plan = props.plan
        val c = props.controller
        val (amount, setAmount) = useState(plan.amount.toString())
        val (day, setDay) = useState("")
        val (confirm, setConfirm) = useState<String?>(null)
        val blocked = props.state.actionBusy || props.state.pendingOperation != null
        div {
            className = ClassName("donor-form")
            p {
                +"Changes require provider confirmation. Pausing stops collection until you explicitly resume. Cancellation takes effect at the end of the current period."
            }
            if (plan.status != "canceled" && !plan.cancelAtPeriodEnd) {
                label {
                    +"Monthly gift amount (USD)"
                    input {
                        type = InputType.text
                        value = amount
                        onChange = { setAmount(it.currentTarget.value) }
                        disabled = blocked
                    }
                }
                button {
                    type = ButtonType.buttonType
                    disabled = blocked
                    onClick = { setConfirm("amount") }
                    +"Change amount"
                }
                label {
                    +"Billing day (1–28, UTC)"
                    input {
                        type = InputType.text
                        value = day
                        onChange = { setDay(it.currentTarget.value) }
                        disabled = blocked || plan.status == "paused"
                    }
                }
                button {
                    type = ButtonType.buttonType
                    disabled = blocked || plan.status == "paused"
                    onClick = { setConfirm("billing_day") }
                    +"Change billing day"
                }
                div {
                    className = ClassName("donor-actions")
                    button {
                        type = ButtonType.buttonType
                        disabled = blocked
                        onClick = { setConfirm(if (plan.status == "paused") "resume" else "pause") }
                        +(if (plan.status == "paused") "Resume monthly gift"
                        else "Pause monthly gift")
                    }
                    button {
                        type = ButtonType.buttonType
                        disabled = blocked
                        onClick = { setConfirm("cancel") }
                        +"Cancel at period end"
                    }
                }
            }
            button {
                type = ButtonType.buttonType
                disabled = blocked
                onClick = { c.manage(plan.id, "portal") }
                +"Update payment method securely ↗"
            }
            confirm?.let { action ->
                div {
                    className = ClassName("donor-confirm")
                    h3 { +"Confirm ${action.replace('_',' ')}" }
                    p {
                        +(when (action) {
                            "amount" ->
                                "Set the full recurring charge to USD $amount. No immediate proration."
                            "billing_day" ->
                                "Move the next eligible charge to UTC day $day by extending the current period. No immediate charge."
                            "pause" ->
                                "Pause collection until you explicitly resume. Paused invoices are voided."
                            "resume" -> "Resume collection on the existing schedule."
                            else -> "Schedule cancellation at the end of the current period."
                        })
                    }
                    DotButton {
                        text = "Confirm request"
                        enabled = !(blocked)
                        onClick = {
                            c.manage(
                                plan.id,
                                action,
                                if (action == "amount") amount else null,
                                if (action == "billing_day") day.toIntOrNull() else null,
                            )
                            setConfirm(null)
                        }
                    }
                    button {
                        type = ButtonType.buttonType
                        onClick = { setConfirm(null) }
                        +"Keep current plan"
                    }
                }
            }
        }
    }
